//! Tracks recently used formula functions (most recent first) in localStorage
//! so the formula autocomplete dropdown can surface them in a "Recent
//! functions" section. Recents are recorded when a completion is accepted and
//! when a formula is committed (which also captures functions typed by hand).

import { getFormulaCompletions } from '@/app/cellEditor/cellEditorLanguage';

const STORAGE_KEY = 'formulaRecentFunctions';
const MAX_RECENT_FUNCTIONS = 10;

export const getRecentFunctionNames = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is string => typeof entry === 'string');
  } catch (_e) {
    return [];
  }
};

export const trackRecentFunction = (label: string) => {
  if (!label) return;
  try {
    const next = [label, ...getRecentFunctionNames().filter((name) => name !== label)].slice(0, MAX_RECENT_FUNCTIONS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (_e) {
    // localStorage unavailable (e.g. privacy mode); recents are best-effort
  }
};

// An identifier immediately followed by "(" — i.e. a function call.
const FUNCTION_CALL_REGEX = /([A-Za-z_][A-Za-z0-9_.]*)\s*\(/g;

/// Records every known formula function used in a committed formula
/// (`codeString` is the formula without the leading `=`).
export const trackFunctionsFromFormula = (codeString: string) => {
  if (!codeString) return;
  const knownFunctions = new Map(getFormulaCompletions().map((c) => [c.label.toLowerCase(), c.label]));
  const used = new Set<string>();
  for (const match of codeString.matchAll(FUNCTION_CALL_REGEX)) {
    const label = knownFunctions.get(match[1].toLowerCase());
    if (label) used.add(label);
  }
  used.forEach(trackRecentFunction);
};
